import React, { useState } from 'react';

function JsonTree({ data, depth = 0 }) {
	const [expanded, setExpanded] = useState(depth < 2);

	if (data === null || data === undefined) {
		return <span className="json-null">null</span>;
	}
	if (typeof data === 'boolean') {
		return <span className="json-bool">{data.toString()}</span>;
	}
	if (typeof data === 'number') {
		return <span className="json-number">{data}</span>;
	}
	if (typeof data === 'string') {
		return <span className="json-string">"{data}"</span>;
	}

	const isArray = Array.isArray(data);
	const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
	const bracket = isArray ? ['[', ']'] : ['{', '}'];

	if (entries.length === 0) {
		return <span>{bracket[0]}{bracket[1]}</span>;
	}

	return (
		<span>
			<span
				className="json-toggle"
				onClick={() => setExpanded(!expanded)}
			>
				{expanded ? '▼' : '▶'} {bracket[0]}
			</span>
			{expanded ? (
				<div className="json-indent">
					{entries.map(([key, value]) => (
						<div key={key} className="json-entry">
							{!isArray && <span className="json-key">"{key}": </span>}
							<JsonTree data={value} depth={depth + 1} />
						</div>
					))}
				</div>
			) : (
				<span className="json-collapsed"> ...{entries.length} items </span>
			)}
			<span>{bracket[1]}</span>
		</span>
	);
}

function DeduplicationSummary({ report }) {
	const claimsSummary = report.claimsSummary || {};
	const dedupDetails = report.deduplicationDetails || [];
	const previousNames = report.previousNames || '';
	const nameDedup = report.nameDeduplicationDetails || [];
	const previousAddresses = report.previousAddresses || [];
	const addressDedup = report.addressDeduplicationDetails || [];
	const consolidatedClaims = report.consolidatedClaims || [];

	const removedDetails = dedupDetails.filter(d => d.action === 'removed');
	const flaggedDetails = dedupDetails.filter(d => d.action === 'flagged');

	return (
		<div className="dedup-summary">
			<h3>Claims Deduplication Summary</h3>

			{/* Overview Stats */}
			<div className="dedup-stats">
				<div className="stat-card">
					<div className="stat-value">{claimsSummary.total || 0}</div>
					<div className="stat-label">Raw Claims</div>
				</div>
				<div className="stat-card">
					<div className="stat-value">{claimsSummary.duplicatesRemoved || 0}</div>
					<div className="stat-label">Duplicates Removed</div>
				</div>
				<div className="stat-card">
					<div className="stat-value">{claimsSummary.deduplicated || 0}</div>
					<div className="stat-label">Unique Claims</div>
				</div>
				<div className="stat-card">
					<div className="stat-value">{claimsSummary.flagged || 0}</div>
					<div className="stat-label">Flagged for Review</div>
				</div>
			</div>

			{/* Removed Duplicates */}
			{removedDetails.length > 0 && (
				<div className="dedup-section">
					<h4>Removed Duplicates ({removedDetails.length})</h4>
					<table className="dedup-table">
						<thead>
							<tr>
								<th>Removed Account #</th>
								<th>Kept Account #</th>
								<th>Match %</th>
								<th>Reason</th>
							</tr>
						</thead>
						<tbody>
							{removedDetails.map((d, i) => (
								<tr key={i}>
									<td><code>{d.removedAccountNumber}</code></td>
									<td><code>{d.keptAccountNumber}</code></td>
									<td><span className="match-pct exact">{d.matchPercentage}%</span></td>
									<td>{d.reason}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Flagged for Review */}
			{flaggedDetails.length > 0 && (
				<div className="dedup-section">
					<h4>Flagged for Review ({flaggedDetails.length})</h4>
					<table className="dedup-table">
						<thead>
							<tr>
								<th>Account # A</th>
								<th>Account # B</th>
								<th>Match %</th>
								<th>Reason</th>
							</tr>
						</thead>
						<tbody>
							{flaggedDetails.map((d, i) => (
								<tr key={i}>
									<td><code>{d.accountNumberA}</code></td>
									<td><code>{d.accountNumberB}</code></td>
									<td><span className="match-pct flagged">{d.matchPercentage}%</span></td>
									<td>{d.reason}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Consolidated Claims */}
			{consolidatedClaims.length > 0 && (
				<div className="dedup-section">
					<h4>Consolidated Claims ({consolidatedClaims.length})</h4>
					<table className="dedup-table">
						<thead>
							<tr>
								<th>Account #</th>
								<th>Source</th>
								<th>Name</th>
								<th>Start Date</th>
								<th>Status</th>
								<th>Flagged Matches</th>
							</tr>
						</thead>
						<tbody>
							{consolidatedClaims.map((c, i) => {
								const name = c.name ? [c.name.title, c.name.forename, c.name.middleName, c.name.surname].filter(Boolean).join(' ') : '-';
								return (
									<tr key={i} className={c.matchedClaims?.length > 0 ? 'flagged-row' : ''}>
										<td><code>{c.accountNumber || '-'}</code></td>
										<td>{c._source || '-'}</td>
										<td>{name}</td>
										<td>{c.startDate || '-'}</td>
										<td>{c.isHistoric ? 'Historic' : 'Active'}</td>
										<td>
											{c.matchedClaims?.length > 0 ? (
												<span className="match-pct flagged">
													{c.matchedClaims.join(', ')} ({c._matchPercentage || '80+'}%)
												</span>
											) : '—'}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Previous Names */}
			<div className="dedup-section">
				<h4>Previous Names</h4>
				{previousNames ? (
					<p className="previous-names">{previousNames}</p>
				) : (
					<p className="no-data">No previous names found.</p>
				)}
				{nameDedup.length > 0 && (
					<div style={{ marginTop: '0.5rem' }}>
						<strong>Name Deduplication Flags:</strong>
						<table className="dedup-table">
							<thead>
								<tr>
									<th>Name A</th>
									<th>Name B</th>
									<th>Match %</th>
									<th>Reason</th>
								</tr>
							</thead>
							<tbody>
								{nameDedup.map((d, i) => (
									<tr key={i}>
										<td>{d.nameA}</td>
										<td>{d.nameB}</td>
										<td><span className="match-pct flagged">{d.matchPercentage}%</span></td>
										<td>{d.reason}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Previous Addresses */}
			<div className="dedup-section">
				<h4>Previous Addresses ({previousAddresses.length})</h4>
				{previousAddresses.length > 0 ? (
					<table className="dedup-table">
						<thead>
							<tr>
								<th>Number</th>
								<th>Name</th>
								<th>Sub Building</th>
								<th>Street 1</th>
								<th>Street 2</th>
								<th>Post Town</th>
								<th>Post Code</th>
								<th>District</th>
								<th>County</th>
							</tr>
						</thead>
						<tbody>
							{previousAddresses.map((a, i) => (
								<tr key={i}>
									<td>{a.PA_Number || '-'}</td>
									<td>{a.PA_Name || '-'}</td>
									<td>{a.PA_Sub_Building || '-'}</td>
									<td>{a.PA_Street_1 || '-'}</td>
									<td>{a.PA_Street_2 || '-'}</td>
									<td>{a.PA_Post_Town || '-'}</td>
									<td>{a.PA_Post_Code || '-'}</td>
									<td>{a.PA_District || '-'}</td>
									<td>{a.PA_County || '-'}</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p className="no-data">No previous addresses found.</p>
				)}
				{addressDedup.length > 0 && (
					<div style={{ marginTop: '0.5rem' }}>
						<strong>Address Deduplication Flags:</strong>
						<table className="dedup-table">
							<thead>
								<tr>
									<th>Address A</th>
									<th>Address B</th>
									<th>Match %</th>
									<th>Reason</th>
								</tr>
							</thead>
							<tbody>
								{addressDedup.map((d, i) => (
									<tr key={i}>
										<td>{d.addressA?.PA_Post_Code} {d.addressA?.PA_Street_1}</td>
										<td>{d.addressB?.PA_Post_Code} {d.addressB?.PA_Street_1}</td>
										<td><span className="match-pct flagged">{d.matchPercentage}%</span></td>
										<td>{d.reason}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

export default function ReportViewer({ report }) {
	const [showRaw, setShowRaw] = useState(false);

	if (!report) return null;

	const hasPdf = report.pdfReport || report.result?.pdfReport;
	const pdfData = report.pdfReport || report.result?.pdfReport;

	const handleDownloadPdf = () => {
		if (!pdfData) return;
		const byteChars = atob(pdfData);
		const byteNumbers = new Array(byteChars.length);
		for (let i = 0; i < byteChars.length; i++) {
			byteNumbers[i] = byteChars.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: 'application/pdf' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'equifax-report.pdf';
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<div className="report-viewer">
			<DeduplicationSummary report={report} />

			<div style={{ marginTop: '1.5rem' }}>
				<h3>Full Credit Report</h3>
				{hasPdf && (
					<button onClick={handleDownloadPdf} className="btn-secondary" style={{ marginBottom: '0.5rem' }}>
						Download PDF Report
					</button>
				)}
				<button
					onClick={() => setShowRaw(!showRaw)}
					className="btn-secondary"
					style={{ marginLeft: '0.5rem', marginBottom: '0.5rem' }}
				>
					{showRaw ? 'Hide' : 'Show'} Raw JSON Response
				</button>
				{showRaw && (
					<div className="json-tree">
						<JsonTree data={report} />
					</div>
				)}
			</div>
		</div>
	);
}
